import React, { useState, useEffect, useCallback } from 'react';
import { logger } from '../utils/logger';
import {
  Brain,
  TrendingUp,
  MessageSquare,
  Users,
  Calendar,
  CheckCircle,
  X,
  AlertTriangle,
  Clock,
  Lightbulb,
  RefreshCw,
  AlertCircle,
  Settings as SettingsIcon,
  Info
} from 'lucide-react';
import { AIInsight, Contact, Activity } from '../types';
import { aiService } from '../services/aiService';
import { openaiService } from '../services/openaiService';
import { aiInsightsService } from '../services/aiInsightsService';
import authService from '../services/authService';
import type { UsageStats } from '../services/aiInsightsService';


interface AIInsightsProps {
  contacts: Contact[];
  activities: Activity[];
  onInsightAction: (insight: AIInsight, action: 'accept' | 'dismiss') => void;
}

type ErrorState = 'none' | 'no_config' | 'invalid_key' | 'no_credits' | 'rate_limit' | 'blocked' | 'network' | 'unknown';

const AIInsights: React.FC<AIInsightsProps> = ({ contacts, activities, onInsightAction }) => {
  const [insights, setInsights] = useState<AIInsight[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'priority' | 'duplicates' | 'suggestions'>('all');
  const [lastGenerated, setLastGenerated] = useState<Date | null>(null);
  const [errorState, setErrorState] = useState<ErrorState>('none');
  const [openaiConfigured, setOpenaiConfigured] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [usageStats, setUsageStats] = useState<UsageStats | null>(null);

  const currentUser = authService.getCurrentSession()?.user;
  const tenantId = currentUser?.id || '';

  useEffect(() => {
    logger.debug('[AIInsights] Component mounted, checking OpenAI configuration...');

    // Force reload from database to get latest config
    const reloadAndCheck = async () => {
      logger.debug('[AIInsights] Reloading OpenAI config from database...');
      const isValid = await openaiService.reloadConfig();
      logger.debug('Config reload result', { isValid });
      checkOpenAIConfiguration();
    };

    reloadAndCheck();
    loadUsageStats();
  }, []);

  useEffect(() => {
    if (openaiConfigured && tenantId) {
      loadInsightsFromSupabase();

      const unsubscribe = aiInsightsService.subscribeToInsights(tenantId, (newInsights) => {
        logger.debug('Realtime insights update received', { count: newInsights.length });
        setInsights(newInsights);
      });

      return () => {
        unsubscribe();
      };
    }
  }, [openaiConfigured, tenantId]);

  const checkOpenAIConfiguration = () => {
    const isConfigured = openaiService.isConfigured();
    setOpenaiConfigured(isConfigured);

    if (isConfigured) {
      logger.debug('OpenAI is configured');
      setErrorState('none');
    } else {
      logger.debug('OpenAI not configured');
      setErrorState('no_config');
    }
  };

  const loadUsageStats = async () => {
    if (!tenantId) return;

    try {
      const stats = await aiInsightsService.getUsageStats(tenantId, currentUser?.id);
      setUsageStats(stats);
    } catch (error) {
      console.error('Error loading usage stats:', error);
    }
  };

  const loadInsightsFromSupabase = async () => {
    if (!tenantId) return;

    try {
      logger.debug('[AIInsights] Loading insights from Supabase...');
      const contactIds = contacts.map(c => c.id);
      const cachedInsights = await aiInsightsService.getValidInsights(tenantId, contactIds);

      if (cachedInsights.length > 0) {
        logger.info(`[AIInsights] Loaded ${cachedInsights.length} cached insights from Supabase`);
        setInsights(cachedInsights);
        setLastGenerated(new Date(Math.max(...cachedInsights.map(i => i.createdAt.getTime()))));
      } else {
        logger.debug('[AIInsights] No cached insights found');
      }
    } catch (error) {
      console.error('Error loading insights from Supabase:', error);
    }
  };

  const testOpenAIConnection = async () => {
    setTestingConnection(true);
    try {
      logger.debug('Testing OpenAI connection');
      const success = await openaiService.testConnection();

      if (success) {
        logger.info('OpenAI connection successful');
        setErrorState('none');
        setOpenaiConfigured(true);
        window.alert('✅ ¡Conexión exitosa! OpenAI está configurado correctamente.');
      } else {
        logger.warn('OpenAI connection failed');
        setErrorState('invalid_key');
      }
    } catch (error: any) {
      console.error('Error testing connection:', error);

      if (error.message?.includes('insufficient_quota')) {
        setErrorState('no_credits');
      } else if (error.message?.includes('rate limit')) {
        setErrorState('rate_limit');
      } else if (error.message?.includes('network') || error.message?.includes('fetch')) {
        setErrorState('network');
      } else {
        setErrorState('invalid_key');
      }
    } finally {
      setTestingConnection(false);
    }
  };

  const generateInsights = async () => {
    if (!openaiConfigured) {
      logger.debug('Cannot generate insights - OpenAI not configured');
      setErrorState('no_config');
      return;
    }

    // Rate limiting is enforced server-side by openai-proxy; a 429 surfaces
    // via the thrown Error and lands in the catch below as errorState='rate_limit'.

    setLoading(true);
    setErrorState('none');

    try {
      logger.debug('Generating insights', { totalContacts: contacts.length });

      const newInsights: AIInsight[] = [];

      // Filter out contacts that are already clients or closed
      const eligibleContacts = contacts.filter(c => {
        // Exclude contacts that are already clients or closed/discarded
        const isActiveProspect = c.leadStatus === 'nuevo' || c.leadStatus === 'en_seguimiento';
        return isActiveProspect;
      });

      logger.debug('Eligible active prospects', { eligible: eligibleContacts.length, total: contacts.length });

      const recentContacts = eligibleContacts
        .filter(c => c.createdAt > new Date(Date.now() - 24 * 60 * 60 * 1000))
        .slice(0, 3);

      logger.debug('Recent active prospects found', { count: recentContacts.length });

      if (recentContacts.length === 0) {
        const allContactsSorted = [...eligibleContacts]
          .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
          .slice(0, 3);

        logger.debug('Analyzing older prospects', { count: allContactsSorted.length });
        recentContacts.push(...allContactsSorted);
      }

      if (recentContacts.length === 0) {
        logger.debug('No active prospects available to analyze');
        setInsights([]);
        setLastGenerated(new Date());
        setLoading(false);

        // Show a friendly message
        if (eligibleContacts.length === 0 && contacts.length > 0) {
          logger.info('[AIInsights] Todos los contactos ya son clientes o están cerrados. ¡Bien hecho!');
        }

        return;
      }

      for (const contact of recentContacts) {
        try {
          logger.debug(`[AIInsights] Analyzing contact: ${contact.fullName}`);
          const insight = await aiService.analyzeFormSubmission(
            { message: contact.notes },
            contact
          );

          if (insight.status !== 'dismissed') {
            newInsights.push(insight);

            // Save to Supabase
            const saved = await aiInsightsService.saveInsight(insight, tenantId);
            if (!saved) {
              logger.warn('[AIInsights] Failed to save insight to Supabase, but continuing...');
            }
          }
        } catch (error: any) {
          console.error(`Error analyzing contact ${contact.fullName}:`, error);

          // Handle specific errors
          if (error.message?.includes('insufficient_quota')) {
            setErrorState('no_credits');
            break;
          } else if (error.message?.includes('rate limit')) {
            setErrorState('rate_limit');
            break;
          } else if (error.message?.includes('blocked')) {
            setErrorState('blocked');
            break;
          }
        }
      }

      // Detect duplicate contacts (local processing) - only among active prospects
      logger.debug('[AIInsights] Detecting duplicates among active prospects...');
      const duplicates = await aiService.detectDuplicateContacts(eligibleContacts);
      for (const duplicate of duplicates.slice(0, 2)) {
        const insight: AIInsight = {
          id: `duplicate-${duplicate.primaryContact.id}`,
          type: 'duplicate_detection',
          contactId: duplicate.primaryContact.id,
          title: 'Posibles Contactos Duplicados',
          description: `Se detectaron ${duplicate.duplicates.length} contactos similares a ${duplicate.primaryContact.fullName} (${duplicate.similarity}% similitud)`,
          confidence: duplicate.similarity,
          data: duplicate,
          createdAt: new Date(),
          status: 'pending'
        };

        newInsights.push(insight);
        await aiInsightsService.saveInsight(insight, tenantId);
      }

      // Meeting time "suggestions" used to be generated here by a hard-coded
      // 10-16h loop dressed up as an AI insight with a fake 85% confidence.
      // Removed 2026-09-17: it was neither an insight nor AI. If we ever want
      // real scheduling suggestions they belong in the calendar module, not
      // in AIInsights.

      setInsights(newInsights);
      setLastGenerated(new Date());
      await loadUsageStats();

      logger.info(`[AIInsights] Generated ${newInsights.length} insights successfully`);
    } catch (error: any) {
      console.error('Error generating insights:', error);

      if (error.message?.includes('OpenAI not configured')) {
        setErrorState('no_config');
      } else if (error.message?.includes('insufficient_quota')) {
        setErrorState('no_credits');
      } else if (error.message?.includes('rate limit')) {
        setErrorState('rate_limit');
      } else if (error.message?.includes('blocked')) {
        setErrorState('blocked');
      } else if (error.message?.includes('network') || error.message?.includes('fetch')) {
        setErrorState('network');
      } else {
        setErrorState('unknown');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (insight: AIInsight, action: 'accept' | 'dismiss') => {
    try {
      // Update in Supabase
      if (insight.id.startsWith('duplicate-') || insight.id.startsWith('meeting-')) {
        // For local insights, just remove from UI
        setInsights(prev => prev.filter(i => i.id !== insight.id));
      } else {
        // For DB insights, update status
        await aiInsightsService.updateInsightStatus(insight.id, action === 'accept' ? 'accepted' : 'dismissed');
      }

      logger.info(`[AIInsights] Insight ${action === 'accept' ? 'accepted' : 'dismissed'}: ${insight.title}`);

      // Call parent handler
      onInsightAction(insight, action);
    } catch (error) {
      console.error('Error handling insight action:', error);
    }
  };

  const getErrorMessage = (): { title: string; message: string; action?: string; actionLabel?: string } => {
    switch (errorState) {
      case 'no_config':
        return {
          title: 'OpenAI No Configurado',
          message: 'Para usar AI Insights, necesitas configurar tu API Key de OpenAI.',
          action: 'config',
          actionLabel: 'Ir a Configuración'
        };
      case 'invalid_key':
        return {
          title: 'API Key Inválida',
          message: 'La API Key de OpenAI configurada no es válida. Verifica la configuración.',
          action: 'config',
          actionLabel: 'Verificar Configuración'
        };
      case 'no_credits':
        return {
          title: 'Sin Créditos OpenAI',
          message: 'Tu cuenta de OpenAI no tiene créditos suficientes. Recarga en platform.openai.com',
          action: 'external',
          actionLabel: 'Abrir OpenAI'
        };
      case 'rate_limit':
        return {
          title: 'Límite de Tasa Excedido',
          message: 'Has excedido el límite de uso de OpenAI. Intenta de nuevo en unos minutos.',
          action: 'retry',
          actionLabel: 'Reintentar'
        };
      case 'blocked':
        return {
          title: 'IA Bloqueada',
          message: 'Has usado la IA demasiadas veces. Espera el tiempo de cooldown.',
        };
      case 'network':
        return {
          title: 'Error de Conexión',
          message: 'No se pudo conectar con OpenAI. Verifica tu conexión a internet.',
          action: 'retry',
          actionLabel: 'Reintentar'
        };
      default:
        return {
          title: 'Error Desconocido',
          message: 'Ocurrió un error al generar insights. Intenta de nuevo.',
          action: 'retry',
          actionLabel: 'Reintentar'
        };
    }
  };

  const handleErrorAction = () => {
    const errorInfo = getErrorMessage();

    if (errorInfo.action === 'config') {
      // Navigate to settings
      const event = new CustomEvent('navigate-to-tab', { detail: { tab: 'configuracion' } });
      window.dispatchEvent(event);
    } else if (errorInfo.action === 'external') {
      window.open('https://platform.openai.com/account/billing', '_blank');
    } else if (errorInfo.action === 'retry') {
      checkOpenAIConfiguration();
      if (openaiConfigured) {
        generateInsights();
      }
    }
  };

  const getInsightIcon = (type: AIInsight['type']) => {
    switch (type) {
      case 'contact_summary': return Brain;
      case 'response_suggestion': return MessageSquare;
      case 'lead_priority': return TrendingUp;
      case 'duplicate_detection': return Users;
      case 'meeting_suggestion': return Calendar;
      default: return Lightbulb;
    }
  };

  const getInsightColor = (type: AIInsight['type']) => {
    switch (type) {
      case 'contact_summary': return 'bg-purple-100 text-purple-600';
      case 'response_suggestion': return 'bg-blue-100 text-blue-600';
      case 'lead_priority': return 'bg-orange-100 text-orange-600';
      case 'duplicate_detection': return 'bg-yellow-100 text-yellow-600';
      case 'meeting_suggestion': return 'bg-green-100 text-green-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const filteredInsights = insights.filter(insight => {
    if (activeTab === 'all') return true;
    if (activeTab === 'priority') return insight.type === 'lead_priority';
    if (activeTab === 'duplicates') return insight.type === 'duplicate_detection';
    if (activeTab === 'suggestions') return ['response_suggestion', 'meeting_suggestion'].includes(insight.type);
    return true;
  });

  const getTimeSinceGeneration = (): string => {
    if (!lastGenerated) return '';

    const now = new Date();
    const diff = now.getTime() - lastGenerated.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));

    if (minutes < 1) return 'Hace menos de 1 minuto';
    if (minutes < 60) return `Hace ${minutes} minutos`;
    if (hours < 24) return `Hace ${hours} horas`;
    return `Hace ${Math.floor(hours / 24)} días`;
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Brain className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Insights de IA</h2>
                <div className="flex items-center space-x-2">
                  <p className="text-sm text-gray-600">
                    {openaiConfigured ? 'OpenAI configurado' : 'Requiere configuración'}
                  </p>
                  {openaiConfigured && (
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                      Activo
                    </span>
                  )}
                  {!openaiConfigured && (
                    <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                      Inactivo
                    </span>
                  )}
                </div>
                {lastGenerated && (
                  <p className="text-xs text-gray-500 mt-1">
                    Última actualización: {getTimeSinceGeneration()}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {usageStats && (
                <div className="text-right mr-3">
                  <p className="text-xs text-gray-500">Tokens hoy</p>
                  <p className="text-sm font-semibold text-gray-900">{usageStats.totalTokensToday.toLocaleString()}</p>
                  <p className="text-xs text-gray-500">${usageStats.totalCostToday.toFixed(4)}</p>
                </div>
              )}
              {openaiConfigured && (
                <button
                  onClick={testOpenAIConnection}
                  disabled={testingConnection}
                  className="flex items-center space-x-1 px-2 py-1 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50"
                  title="Probar conexión con OpenAI"
                >
                  {testingConnection ? (
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-600"></div>
                  ) : (
                    <AlertCircle className="w-3 h-3" />
                  )}
                  <span>Test</span>
                </button>
              )}
              <button
                onClick={generateInsights}
                disabled={loading || !openaiConfigured}
                className="flex items-center space-x-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                title={openaiConfigured ? 'Genera insights con IA (consume tokens de OpenAI)' : 'Requiere configuración de OpenAI'}
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                <span>{loading ? 'Analizando...' : 'Generar Insights'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Error Banner */}
        {errorState !== 'none' && (
          <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-red-900 mb-1">
                  {getErrorMessage().title}
                </h3>
                <p className="text-sm text-red-700 mb-3">
                  {getErrorMessage().message}
                </p>
                {getErrorMessage().action && (
                  <button
                    onClick={handleErrorAction}
                    className="px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                  >
                    {getErrorMessage().actionLabel}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        {openaiConfigured && (
          <div className="px-6 py-3 border-b border-gray-200">
            <div className="flex space-x-1">
              {[
                { id: 'all', label: 'Todos', count: insights.length },
                { id: 'priority', label: 'Prioridad', count: insights.filter(i => i.type === 'lead_priority').length },
                { id: 'duplicates', label: 'Duplicados', count: insights.filter(i => i.type === 'duplicate_detection').length },
                { id: 'suggestions', label: 'Sugerencias', count: insights.filter(i => ['response_suggestion', 'meeting_suggestion'].includes(i.type)).length }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    activeTab === tab.id
                      ? 'bg-purple-100 text-purple-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  {tab.label} {tab.count > 0 && (
                    <span className="ml-1 px-2 py-0.5 bg-gray-200 text-gray-700 rounded-full text-xs">
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
              <p className="text-gray-500">Generando insights con IA...</p>
              <p className="text-xs text-gray-400 mt-2">
                Esto puede tardar unos segundos y consumirá tokens de OpenAI
              </p>
            </div>
          ) : !openaiConfigured ? (
            <div className="text-center py-8">
              <SettingsIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">OpenAI No Configurado</h3>
              <p className="text-gray-500 mb-4 max-w-md mx-auto">
                Para usar AI Insights, necesitas configurar tu API Key de OpenAI en la sección de Configuración.
              </p>
              <button
                onClick={() => {
                  const event = new CustomEvent('navigate-to-tab', { detail: { tab: 'configuracion' } });
                  window.dispatchEvent(event);
                }}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors inline-flex items-center space-x-2"
              >
                <SettingsIcon className="w-4 h-4" />
                <span>Configurar OpenAI</span>
              </button>
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg max-w-md mx-auto">
                <div className="flex items-start space-x-2">
                  <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-left">
                    <p className="text-sm text-blue-900 font-medium mb-1">Cómo obtener tu API Key</p>
                    <ol className="text-xs text-blue-700 space-y-1 list-decimal list-inside">
                      <li>Ve a platform.openai.com</li>
                      <li>Crea una cuenta o inicia sesión</li>
                      <li>Ve a API Keys en el menú</li>
                      <li>Crea una nueva API Key</li>
                      <li>Copia y pega en Configuración</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          ) : filteredInsights.length > 0 ? (
            <div className="space-y-4">
              {filteredInsights.map(insight => {
                const Icon = getInsightIcon(insight.type);
                const colorClass = getInsightColor(insight.type);

                return (
                  <div key={insight.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        <div className={`p-2 rounded-lg ${colorClass}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <h3 className="text-sm font-medium text-gray-900">{insight.title}</h3>
                            <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">
                              {insight.confidence}% confianza
                            </span>
                          </div>

                          {insight.contactId && (
                            <div className="mb-2">
                              {(() => {
                                const associatedEntity = contacts.find(c => c.id === insight.contactId);

                                if (associatedEntity) {
                                  return (
                                    <p className="text-xs text-blue-600 font-medium">
                                      Para: {associatedEntity.fullName} ({associatedEntity.company})
                                    </p>
                                  );
                                }

                                return (
                                  <p className="text-xs text-gray-500">
                                    Para: Contacto ID {insight.contactId}
                                  </p>
                                );
                              })()}
                            </div>
                          )}

                          <p className="text-sm text-gray-600 mb-2">{insight.description}</p>

                          {insight.type === 'duplicate_detection' && insight.data && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mt-2">
                              <p className="text-xs text-yellow-800 mb-2">Contactos similares encontrados:</p>
                              <div className="space-y-1">
                                {insight.data.duplicates.map((dup: any, idx: number) => (
                                  <p key={idx} className="text-xs text-yellow-700">
                                    • {dup.fullName} ({dup.email})
                                  </p>
                                ))}
                              </div>
                            </div>
                          )}

                          {insight.type === 'meeting_suggestion' && insight.data?.suggestions && (
                            <div className="bg-green-50 border border-green-200 rounded p-3 mt-2">
                              <p className="text-xs text-green-800 mb-2">Horarios sugeridos:</p>
                              <div className="space-y-1">
                                {insight.data.suggestions.map((suggestion: string, idx: number) => (
                                  <p key={idx} className="text-xs text-green-700">• {suggestion}</p>
                                ))}
                              </div>
                            </div>
                          )}

                          <div className="flex items-center space-x-2 mt-3 text-xs text-gray-500">
                            <Clock className="w-3 h-3" />
                            <span>{insight.createdAt.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleAction(insight, 'accept')}
                          className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-colors"
                          title="Aceptar sugerencia"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleAction(insight, 'dismiss')}
                          className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                          title="Descartar"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <Brain className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No hay insights disponibles</h3>
              {contacts.length === 0 ? (
                <p className="text-gray-500 mb-4">No hay contactos para analizar. Crea algunos leads primero.</p>
              ) : contacts.filter(c => c.leadStatus === 'nuevo' || c.leadStatus === 'en_seguimiento').length === 0 ? (
                <div className="max-w-md mx-auto">
                  <p className="text-gray-500 mb-2">Todos tus contactos ya son clientes o están cerrados.</p>
                  <p className="text-sm text-green-600 font-medium">¡Excelente trabajo! No hay leads activos pendientes de análisis.</p>
                </div>
              ) : (
                <>
                  <p className="text-gray-500 mb-4">Haz clic en "Generar Insights" para analizar tus leads activos</p>
                  <button
                    onClick={generateInsights}
                    disabled={loading}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                  >
                    {loading ? 'Generando...' : 'Generar Insights'}
                  </button>
                </>
              )}
            </div>
          )}

          {insights.length > 0 && usageStats && (
            <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="w-4 h-4 text-orange-600" />
                <span className="text-sm text-orange-700">
                  Los análisis de IA consumen tokens de OpenAI. Hoy has usado {usageStats.totalTokensToday.toLocaleString()} tokens (${usageStats.totalCostToday.toFixed(4)})
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AIInsights;
